import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import PublicRoute from './components/PublicRoute';
import PublicRouteNoHeader from './components/PublicRouteNoHeader';
import { routes } from './AppRoutes';

// ── Constantes ────────────────────────────────────────────────────────────────
const DEFAULT_PAGE   = 'home';
const AUTH_LANDING   = 'platform';
const UNAUTH_LANDING = 'home';
const LOGIN_PAGE     = 'login';

const STYLES = {
  container: { minHeight: '100vh', backgroundColor: '#f9fafb' },
  loading:   { minHeight: '100vh', display: 'flex', alignItems: 'center',
               justifyContent: 'center', backgroundColor: '#f9fafb' },
};

// ── Helpers de URL ────────────────────────────────────────────────────────────
const getFullPath = () => window.location.pathname.slice(1) || DEFAULT_PAGE;
const pushPath    = (path) => window.history.pushState(null, '', `/${path}`);

// ── Router ────────────────────────────────────────────────────────────────────
const matchRoute = (route, fullPath) => {
  const routeParts  = route.path.split('/');
  const actualParts = fullPath.split('/').filter(Boolean);
  const params = {};
  let i = 0;

  for (; i < routeParts.length; i++) {
    if (i >= actualParts.length) return { match: false };
    if (routeParts[i].startsWith(':')) params[routeParts[i].slice(1)] = actualParts[i];
    else if (routeParts[i] !== actualParts[i]) return { match: false };
  }

  return route.hasNestedRoutes
    ? { match: true, params, subPath: actualParts.slice(i).join('/') }
    : { match: i === actualParts.length, params, subPath: '' };
};

const findMatchingRoute = (fullPath) => {
  for (const route of routes) {
    const { match, params, subPath } = matchRoute(route, fullPath);
    if (match) return { route, params, subPath };
  }
  return { route: null, params: {}, subPath: '' };
};

// ── Componentes ───────────────────────────────────────────────────────────────
const LoadingScreen = React.memo(() => (
  <div style={STYLES.loading}><p>Cargando...</p></div>
));
LoadingScreen.displayName = 'LoadingScreen';

const PageRenderer = React.memo(({ route, onNavigate, subPath, params }) => {
  if (!route) return null;
  const Component = route.component;

  if (route.isPrivate) return (
    <ProtectedRoute onNavigate={onNavigate}>
      <Component subPath={subPath} {...params} />
    </ProtectedRoute>
  );
  if (route.isPublic) return (
    <PublicRoute onNavigate={onNavigate}>
      <Component onNavigate={onNavigate} {...params} />
    </PublicRoute>
  );
  if (route.isPublicNoHeader) return (
    <PublicRouteNoHeader onNavigate={onNavigate}>
      <Component onNavigate={onNavigate} {...params} />
    </PublicRouteNoHeader>
  );
  return <Component onNavigate={onNavigate} {...params} />;
});
PageRenderer.displayName = 'PageRenderer';

// ── Hook de navegación ────────────────────────────────────────────────────────
const useNavigation = (currentUser, loading) => {
  const [currentFullPath, setCurrentFullPath] = useState(getFullPath);

  // Destructuring directo: evita variables intermedias
  const { route: currentRoute, params: currentParams, subPath: currentSubPath } =
    useMemo(() => findMatchingRoute(currentFullPath), [currentFullPath]);

  const handleNavigate = useCallback((targetPath) => {
    setCurrentFullPath(targetPath);
    pushPath(targetPath);
  }, []);

  // Sincronización con botones atrás/adelante del navegador
  useEffect(() => {
    const onPopState = () => setCurrentFullPath(getFullPath());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // Toda la lógica de redirección en un solo efecto con orden explícito
  useEffect(() => {
    if (loading) return;

    const isDefault = !currentFullPath || currentFullPath === DEFAULT_PAGE;

    // 1. Página por defecto → landing según auth
    if (isDefault) {
      handleNavigate(currentUser ? AUTH_LANDING : UNAUTH_LANDING);
      return;
    }
    // 2. Ruta privada sin sesión → login
    if (currentRoute?.requiresAuth && !currentUser) {
      handleNavigate(LOGIN_PAGE);
      return;
    }
    // 3. Ruta con redirectIfAuth (login/register) con sesión activa
    if (currentRoute?.redirectIfAuth && currentUser) {
      handleNavigate(currentRoute.redirectIfAuth);
      return;
    }
    // 4. Fallback post-login desde /login
    if (currentUser && currentFullPath === LOGIN_PAGE) {
      handleNavigate(AUTH_LANDING);
    }
  }, [currentUser, loading, currentFullPath, currentRoute, handleNavigate]);

  return { currentRoute, currentSubPath, currentParams, handleNavigate };
};

// ── App ───────────────────────────────────────────────────────────────────────
const AppContent = () => {
  const { currentUser, loading } = useAuth();
  const { currentRoute, currentSubPath, currentParams, handleNavigate } =
    useNavigation(currentUser, loading);

  if (loading) return <LoadingScreen />;

  return (
    <div style={STYLES.container}>
      <PageRenderer
        route={currentRoute}
        onNavigate={handleNavigate}
        subPath={currentSubPath}
        params={currentParams}
      />
    </div>
  );
};

const App = () => (
  <AuthProvider>
    <AppContent />
  </AuthProvider>
);

export default App;